// studentapps/NotesApp.js
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  CorporateFare as CorporateIcon
} from '@mui/icons-material';

const NotesApp = () => {
  const [notes, setNotes] = useState([
    {
      id: 1,
      title: 'Math Formulas',
      content: 'Quadratic formula: x = [-b ± √(b² - 4ac)] / 2a\n\nPythagorean theorem: a² + b² = c²\n\nDerivative rules and basic integration formulas.',
      subject: 'Mathematics',
      tags: ['formulas', 'algebra', 'calculus'],
      lastModified: '2024-01-10',
      color: '#ff4d4d'
    },
    {
      id: 2,
      title: 'Physics Laws',
      content: 'Newton\'s Laws of Motion:\n1. An object at rest stays at rest...\n2. F = ma\n3. For every action, there is an equal and opposite reaction.',
      subject: 'Physics',
      tags: ['laws', 'motion', 'mechanics'],
      lastModified: '2024-01-09',
      color: '#0a1128'
    },
    {
      id: 3,
      title: 'Chemistry Basics',
      content: 'Periodic table trends:\n- Atomic radius decreases across a period\n- Electronegativity increases across a period\n- Ionization energy increases across a period',
      subject: 'Chemistry',
      tags: ['periodic-table', 'trends'],
      lastModified: '2024-01-08',
      color: '#8b0000'
    }
  ]);

  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [currentNote, setCurrentNote] = useState({
    title: '',
    content: '',
    subject: '',
    tags: [],
    color: '#0a1128'
  });

  const filteredNotes = notes.filter(note =>
    note.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleAddNote = () => {
    if (currentNote.title && currentNote.content) {
      const note = {
        id: Date.now(),
        ...currentNote,
        lastModified: new Date().toISOString().split('T')[0]
      };
      setNotes([...notes, note]);
      setCurrentNote({ title: '', content: '', subject: '', tags: [], color: '#0a1128' });
      setOpenDialog(false);
    }
  };

  const handleDeleteNote = (id) => {
    setNotes(notes.filter(note => note.id !== id));
  };

  const subjects = ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'Computer Science', 'History', 'Literature'];
  const colors = ['#0a1128', '#8b0000', '#ff4d4d', '#1a1f3d', '#2d4a8a', '#4a5568', '#2d3748'];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="700" gutterBottom>
            Study Notes
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Organize and access your learning materials
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          size="large"
        >
          New Note
        </Button>
      </Box>

      {/* Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search notes by title, content, subject, or tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            size="small"
          />
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Notes List */}
        <Grid item xs={12} lg={8}>
          <Grid container spacing={2}>
            {filteredNotes.map((note) => (
              <Grid item xs={12} sm={6} md={4} key={note.id}>
                <Paper 
                  sx={{ 
                    p: 3, 
                    height: '100%', 
                    borderRadius: 3,
                    borderLeft: `4px solid ${note.color}`,
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
                      {note.title}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteNote(note.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                  
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 2, 
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {note.content}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                    <Chip label={note.subject} size="small" color="primary" />
                    {note.tags.slice(0, 2).map((tag, index) => (
                      <Chip key={index} label={tag} size="small" variant="outlined" />
                    ))}
                    {note.tags.length > 2 && (
                      <Chip label={`+${note.tags.length - 2}`} size="small" variant="outlined" />
                    )}
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {note.lastModified}
                    </Typography>
                    <IconButton size="small" color="primary">
                      <EditIcon />
                    </IconButton>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {filteredNotes.length === 0 && (
            <Card sx={{ textAlign: 'center', py: 6 }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No notes found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchTerm ? 'Try adjusting your search terms' : 'Create your first note to get started'}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Statistics and Quick Actions */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Note Statistics
                </Typography>
                <Chip 
                  label="Zolang Nexora" 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  icon={<CorporateIcon />}
                />
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Total Notes</Typography>
                  <Chip label={notes.length} size="small" color="primary" />
                </Box>
                
                {subjects.map(subject => {
                  const count = notes.filter(n => n.subject === subject).length;
                  return count > 0 ? (
                    <Box key={subject} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">{subject}</Typography>
                      <Chip label={count} size="small" variant="outlined" />
                    </Box>
                  ) : null;
                })}
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button variant="outlined" fullWidth startIcon={<AddIcon />}>
                  Import Notes
                </Button>
                <Button variant="outlined" fullWidth startIcon={<EditIcon />}>
                  Export All
                </Button>
                <Button variant="outlined" fullWidth>
                  Organize by Subject
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add Note Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h5" fontWeight="600">
            Create New Note
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={8}>
              <TextField
                autoFocus
                fullWidth
                label="Note Title"
                variant="outlined"
                value={currentNote.title}
                onChange={(e) => setCurrentNote({...currentNote, title: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                label="Color"
                variant="outlined"
                value={currentNote.color}
                onChange={(e) => setCurrentNote({...currentNote, color: e.target.value})}
              >
                {colors.map((color) => (
                  <MenuItem key={color} value={color}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 20, height: 20, backgroundColor: color, borderRadius: 1 }} />
                      <Typography>{color}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Subject"
                variant="outlined"
                value={currentNote.subject}
                onChange={(e) => setCurrentNote({...currentNote, subject: e.target.value})}
              >
                {subjects.map((subject) => (
                  <MenuItem key={subject} value={subject}>
                    {subject}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Note Content"
                multiline
                rows={6}
                variant="outlined"
                value={currentNote.content}
                onChange={(e) => setCurrentNote({...currentNote, content: e.target.value})}
                placeholder="Start typing your notes here..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tags (comma-separated)"
                variant="outlined"
                placeholder="formulas, important, exam"
                onChange={(e) => setCurrentNote({...currentNote, tags: e.target.value.split(',').map(tag => tag.trim())})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleAddNote} variant="contained">
            Save Note
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotesApp;